// 2. Third-party library imports
import { filter, get } from 'lodash-es';
import {
    all,
    call,
    fork,
    put,
    select,
    takeLatest,
    delay,
} from 'redux-saga/effects';

// 3. Constants
import {
    API_NAME,
    maxAPIRefetchCount,
    refetchAPIDelay,
} from '../../config/config';
import { IMPORT_HISTORY_PAGE } from '../../config/tableAndPageConstants';

// 5. Project-specific modules - components
import { _queries as queries } from '../../graphql/queries.graphql';

// 6. Utility functions
import { checkShouldRequestRefetch } from '../../utils/commonFunctions';
import { DynamicObject } from '../../utils/commonInterfaces';
import client, {
    post,
    get as amplifyGet,
    delApi
} from 'src/api';

// 7. Redux actions
import {
    getImportHistoryDataErrorAction,
    getImportHistoryDataErrorsAndWarningsErrorAction,
    getImportHistoryDataErrorsAndWarningsSuccessAction,
    getImportHistoryDataSuccessAction,
    getImportHistoryErrorAction,
    getImportHistorySuccessAction,
    setImportHistorySelectedIdSuccessAction,
    getImportHistoryRequestAction,
    getXeroConfigurationRequestAction,
    getCheckImportJobStatusRequestAction,
    xeroManualSyncRequestAction,
    xeroDisconnectRequestAction,
    xeroConnectRequestAction,
    getImportHistoryDataErrorsAndWarningsRequestAction,
    setImportHistorySelectedIdRequestAction,
    getImportHistoryDataRequestAction,
    downloadTemplateFileRequestAction,
    uploadTemplateFileRequestAction,
    uploadRemittanceAdviceTemplateFileRequestAction,
    getImportHistoryList,
    getMYOBConfigurationRequestAction,
    myobManualSyncRequestAction,
    myobConnectMYOBCompanyRequestAction,
    myobDisconnectRequestAction,
    myobConnectRequestAction,
    getMYOBCompanySettingsRequestAction
} from './importSlice';
import { EntityType, ImportActionTypes, ImportConfig, ImportConfigs } from './types';

/**
 * Function that fetches the presigned url used for importing an excel file.
 * @param sagaPayload
 */
function* handleUploadTemplateFileRequest(sagaPayload: any) {
    const { payload } = sagaPayload;
    const type = get(payload, 'type');
    const remittanceAdviceId = get(payload, 'remittanceAdviceId');
    const callback = get(payload, 'callback');
    let url = '/import/upload/file/url';

    if (type && type === 'pp' || type === 'mc') {
        url = `${url}?type=${type}`
    }

    if (type && type === 'ra' && remittanceAdviceId) {
        url = `${url}?type=${type}&remittanceAdviceId=${remittanceAdviceId}`
    }

    try {
        // To call async functions, use redux-saga's `call()`.
        const { body } = type && type === 'pp'
        ? yield call(amplifyGet,
            {
                apiName: API_NAME,
                path: url,
                options: {
                    queryParams: {
                        type: type
                    }
                }
            }
        )
        : yield call(amplifyGet, 
            {
                apiName: API_NAME,
                path: url
            }
        );

        const res: DynamicObject = yield body.json();
        res.IsSuccess = true;

        if (callback) {
            callback(res);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function that fetches the presigned url for downloading the template file.
 * @param sagaPayload
 */
function* handleDownloadTemplateFileRequest(sagaPayload: any) {
    const { payload } = sagaPayload;
    const fileName = get(payload, 'fileName');
    const callback = get(payload, 'callback');
    
    try {
        // To call async functions, use redux-saga's `call()`.
        const { body } = yield call(amplifyGet, {
            apiName: API_NAME,
            path: '/import/download/template/url',
            options: {
                queryParams: {
                    FileName: fileName
                }
            }
        });

        const res: DynamicObject = yield body.json();
        res.IsSuccess = true;

        if (callback) {
            callback(res);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

let refetchCount = 0;
/**
 * Function for getting the Import history data for company.
 */
function* handleGetImportHistoryRequest({ payload: sagaPayload }: any) {
    const { ...payload } = sagaPayload;
    let { refetchCount } = sagaPayload;
    refetchCount = refetchCount || 0;
    const errorMessage = "Error fetching import history list. Please try again later.";
    const { importJobId, currentPage, pageSize, entityType } = sagaPayload;
    const importConfig: ImportConfig = ImportConfigs[entityType as EntityType];

    try {
        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: importConfig.getImportQuery,
                variables: {
                    ImportJobId: importJobId,
                    Skip: currentPage * IMPORT_HISTORY_PAGE.pageSize,
                    PageSize: pageSize
                }
            }
        );
 
        const { ImportJobStates } = get(
            res.data, importConfig.getImportQueryName
        );
        const responsePayload = {
            data: ImportJobStates,
            pageData: {
                pageSize: pageSize,
                currentPage: currentPage,
                hasNextPage:
                    !(ImportJobStates.length < pageSize) &&
                    !(pageSize < IMPORT_HISTORY_PAGE.pageSize),
            },
        };
        refetchCount = 0;
        yield put(getImportHistorySuccessAction(responsePayload));
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }
 
        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(getImportHistoryRequestAction(entityType, currentPage, pageSize));
        } else {
            yield put(getImportHistoryErrorAction([errorMessage]));
        }
    }
}

/**
 * Function for getting the Import History details data.
 */
function* handleGetImportHistoryDataRequest({ payload: importHistoryId }: any) {
    const errorMessage = "Error fetching import history details. Please try again later.";

    try {
        // To call async functions, use redux-saga's `call()`.
        const importHistoryDataList: DynamicObject[] = yield select(
            getImportHistoryList
        );
        const importHistoryData = get(
            filter(importHistoryDataList, ['Id', importHistoryId]),
            0
        );

        if (importHistoryData) {
            const responsePayload = {
                record: importHistoryData,
            };

            yield put(getImportHistoryDataSuccessAction(responsePayload));
        } else {
            yield put(getImportHistoryDataErrorAction([errorMessage]));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getImportHistoryDataErrorAction([errorMessage]));
    }
}

/**
 * Function that sets the import history selected Id. Also to trigger the callback and trigger the success action.
 */
function* handleSetImportHistorySelectedIdRequest({ payload }: any) {
    const { importHistoryId, callback } = payload;
    yield put(setImportHistorySelectedIdSuccessAction(importHistoryId));

    if (callback) {
        callback();
    }
}

/**
 * Function for getting the errors and warnings data inside import history details.
 */
function* handleGetImportHistoryDataErrorsAndWarningsRequest({
    payload: sagaPayload,
}: any) {
    const errorMessage = "Error fetching import history details. Please try again later.";

    try {
        // To call async functions, use redux-saga's `call()`.
        const { importJobId, currentPage, pageSize, entityType } = sagaPayload;
        const importConfig: ImportConfig = ImportConfigs[entityType as EntityType];
        
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: importConfig.getMessageQuery, 
                variables: {
                    ImportJobId: importJobId,
                    Skip:
                        currentPage *
                        IMPORT_HISTORY_PAGE.ERRORS_AND_WARNINGS.pageSize,
                    PageSize: pageSize,
                }
            }
        );

        const { ImportStagingMessages } = get(
            res.data,
            importConfig.getMessageQueryName
        );

        if (ImportStagingMessages) {
            const responsePayload = {
                data: ImportStagingMessages,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(ImportStagingMessages.length < pageSize) &&
                        !(
                            pageSize <
                            IMPORT_HISTORY_PAGE.ERRORS_AND_WARNINGS.pageSize
                        ),
                },
            };

            yield put(
                getImportHistoryDataErrorsAndWarningsSuccessAction(
                    responsePayload
                )
            );
        } else {
            yield put(
                getImportHistoryDataErrorsAndWarningsErrorAction([errorMessage])
            );
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(
            getImportHistoryDataErrorsAndWarningsErrorAction([errorMessage])
        );
    }
}

/**
 * Function for getting xero configuration.
 */
function* handleGetXeroConfigurationRequest({ payload: sagaPayload }: any) {
    const { callback } = sagaPayload;
    const errorMessage = "Error fetching xero configuration. Please try again later.";

    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_XERO_CONFIGURATION_FOR_IMPORT
            }
        );

        const xeroConfigData = get(res.data, 'GetXeroConfigurationForImport');

        if (callback) {
            const returnData: DynamicObject = {
                xeroConfiguration: xeroConfigData,
                IsSuccess: true,
            };

            callback(returnData);
        }
    } catch (err) {
        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(getXeroConfigurationRequestAction(callback));
        } else {
            if (callback) {
                let returnData: any;

                if (err instanceof Error && 'response' in err && (err as any).response.body) {
                    returnData = JSON.parse((err as any).response.body);
                } else if (err instanceof Error) {
                    returnData = { Messages: [err.message] };
                } else {
                    returnData = { Messages: [errorMessage] };
                }

                returnData.IsSuccess = false;
                callback(returnData);
            }
        }
    }
}

/**
 * Function that initiates a connection to xero api.
 * @param sagaPayload
 */
function* handleXeroConnectRequest({ payload: sagaPayload}: any) {
    const { callback } = sagaPayload;
    const errorMessage = "Error encountered when connecting to Xero. Please try again later.";

    try {
        // To call async functions, use redux-saga's `call()`.
        const { body } = yield call(amplifyGet, {
            apiName: API_NAME,
            path: '/import/xero/connect'
        });
        
        const res: DynamicObject = yield body.json();
        res.IsSuccess = true;
        if (callback) {
            callback(res);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: [errorMessage] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function that disconnects the company from xero api.
 * @param sagaPayload
 */
function* handleXeroDisconnectRequest({ payload }: any) {
    const { connectionId, callback } = payload;

    try {
        // To call async functions, use redux-saga's `call()`.
        const queryString = new URLSearchParams({
           ConnectionId: JSON.stringify(connectionId),
        }).toString();

        //yield del({
        //    apiName: API_NAME,
        //    path: `/import/xero/connect?${queryString}`
        //});
        yield call(delApi, `/import/xero/connect`, {
                ConnectionId: connectionId
            });
        
        const res: DynamicObject = {
            IsSuccess: true
        }

        if (callback) {
            callback(res);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function that syncs the xero account.
 * @param sagaPayload
 */
function* handleXeroManualSyncRequest({ payload }: any) {
    const { callback } = payload;

    try {
        // To call async functions, use redux-saga's `call()`.
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/import/xero/manualsync'
        });

        const res: DynamicObject = yield body.json();
        res.IsSuccess = true;

        if (callback) {
            callback(res);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function that initiates a connection to myob api.
 * @param sagaPayload
 */
function* handleMYOBConnectRequest({ payload }: any) {
    const { callback } = payload;
    const errorMessage = "An error was encountered while connecting to MYOB. Please try again later.";

    try {
        // To call async functions, use redux-saga's `call()`.
        const { body } = yield call(amplifyGet, {
            apiName: API_NAME,
            path: '/import/myob/connect'
        });

        const res: DynamicObject = yield body.json();
        res.IsSuccess = true;

        if (callback) {
            callback(res);
        }
    } catch (err) {
        if (callback) {
            const returnData = {
                Messages: [errorMessage], // or [] if no error
                IsSuccess: false,
            };
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function that disconnects the company from myob api.
 * @param sagaPayload
 */
function* handleMYOBDisconnectRequest({ payload }: any) {
    const { connectionId, callback } = payload;
    const errorMessage = "An error was encountered while disconnecting from MYOB company. Please try again later.";

    try {
        // To call async functions, use redux-saga's `call()`.
        yield call(delApi, `/import/myob/connect`,{
            ConnectionId: connectionId
        });

        const res: DynamicObject = {
            IsSuccess: true
        }

        if (callback) {
            callback(res);
        }
    } catch (err) {
        if (callback) {
            const returnData = {
                Messages: [errorMessage], // or [] if no error
                IsSuccess: false,
            };
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function for getting myob configuration.
 */
function* handleGetMYOBConfigurationRequest({ payload }: any) {
    const { callback } = payload;
    const errorMessage = "Error fetching MYOB configuration. Please try again later.";

    try {
        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_MYOB_CONFIGURATION_FOR_IMPORT
            }
        );

        const myobConfigData = get(res.data, 'GetMYOBConfigurationForImport');
        
        if (callback) {
            const returnData: DynamicObject = {
                myobConfigData: myobConfigData,
                IsSuccess: true,
            };

            callback(returnData);
        }
    } catch (err) {
        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;
            yield delay(refetchAPIDelay);
            yield put(getMYOBConfigurationRequestAction(callback));
        } else {
            if (callback) {
                const returnData = {
                    Messages: [errorMessage], // or [] if no error
                    IsSuccess: false,
                };

                callback(returnData);
            }
        }
    }
}

/**
 * Function that initiates a connection to myob api.
 * @param sagaPayload
 */
function* handleMYOBConnectCompanyRequest({ payload }: any) {
    const { selectedMYOBCompany, callback } = payload;
    const errorMessage = "Error encountered when connecting to a MYOB company. Please try again later.";

    try {
        // To call async functions, use redux-saga's `call()`.
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/import/myob/company/save',
            options: {
                body: {
                    MYOBCompanyId: selectedMYOBCompany.MYOBCompanyId,
                    MYOBCompanySettings: selectedMYOBCompany.MYOBCompanySettings
                },
            }
        });

        const res: DynamicObject = yield body.json();

        if (callback) {
            res.IsSuccess = true;

            callback(res);
        }
    } catch (err) {
        if (callback) {
            const returnData = {
                Messages: [errorMessage], // or [] if no error
                IsSuccess: false,
            };
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function that syncs the myob account.
 * @param sagaPayload
 */
function* handleMYOBManualSyncRequest({ payload }: any) {
    const { callback } = payload;
    const errorMessage = "An error occurred while syncing MYOB account. Please try again later.";

    try {
        // To call async functions, use redux-saga's `call()`.
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/import/myob/manualsync',
        });

        const res: DynamicObject = yield body.json();
        res.IsSuccess = true;

        if (callback) {
            callback(res);
        }
    } catch (err) {
        if (callback) {
            const returnData = {
                Messages: [errorMessage], // or [] if no error
                IsSuccess: false,
            };
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function that check import job status.
 * @param sagaPayload
 */
function* handleCheckImportJobStatusRequest({ payload: sagaPayload }: any) {
    const { callback } = sagaPayload;
    
    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.CHECK_IMPORT_JOB_STATUS_FOR_COMPANY,
                variables: {}
            }
        );

        if (callback) {
            callback(get(res, 'data.CheckImportJobStatusForCompany'));
        }
    } catch (err) {
        if (callback) {
            let returnData: DynamicObject;

            if (typeof err === 'object' && err !== null && 'response' in err) {
                const errorResponse = err as { response: { data: any } };

                returnData = get(errorResponse.response, 'data')
                    ? errorResponse.response.data
                    : { Messages: ['An error occurred'] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function for getting myob company settings.
 */
function* handleGetMYOBCompanySettingsRequest({ payload: sagaPayload }: any) {
    const errorMessage =
        'Error fetching MYOB company settings. Please try again later.';
    const { callback } = sagaPayload;
    try {
        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_MYOB_COMPANY_SETTINGS_FOR_IMPORT
            }
        );

        const myobCompanySettingsData = get(res.data, 'GetMYOBSettingsForCompany');

        if (callback) {
            const returnData: DynamicObject = {
                myobCompanySettingsData: myobCompanySettingsData,
                IsSuccess: true,
            };

            callback(returnData);
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }
 
        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(getMYOBConfigurationRequestAction(callback));
        } else {
            if (callback) {
                const returnData = {
                    Messages: [errorMessage], // or [] if no error
                    IsSuccess: false,
                };

                callback(returnData);
            }
        }
    }
}

// This is our watcher function. We use `take*()` functions to watch Redux for a specific action
// type, and run our saga, for example the `handleFetch()` saga above.
function* watchUploadTemplateFileRequest() {
    yield takeLatest(
        uploadTemplateFileRequestAction.type,
        handleUploadTemplateFileRequest
    );
}

// This is our watcher function. We use `take*()` functions to watch Redux for a specific action
// type, and run our saga, for example the `handleFetch()` saga above.
function* watchRAUploadTemplateFileRequest() {
    yield takeLatest(
        uploadRemittanceAdviceTemplateFileRequestAction.type,
        handleUploadTemplateFileRequest
    );
}

function* watchDownloadTemplateFileRequest() {
    yield takeLatest(
        downloadTemplateFileRequestAction.type,
        handleDownloadTemplateFileRequest
    );
}

function* watchGetImportHistoryRequest() {
    yield takeLatest(
        getImportHistoryRequestAction.type,
        handleGetImportHistoryRequest
    );
}

function* watchGetImportHistoryDataRequest() {
    yield takeLatest(
        getImportHistoryDataRequestAction.type,
        handleGetImportHistoryDataRequest
    );
}

function* watchSetImportHistorySelectedIdRequest() {
    yield takeLatest(
        setImportHistorySelectedIdRequestAction.type,
        handleSetImportHistorySelectedIdRequest
    );
}

function* watchGetImportHistoryDataErrorsAndWarningsRequest() {
    yield takeLatest(
        getImportHistoryDataErrorsAndWarningsRequestAction.type,
        handleGetImportHistoryDataErrorsAndWarningsRequest
    );
}

function* watchGetXeroConfigurationRequest() {
    yield takeLatest(
        getXeroConfigurationRequestAction.type,
        handleGetXeroConfigurationRequest
    );
}

function* watchXeroConnectRequest() {
    yield takeLatest(
        xeroConnectRequestAction.type,
        handleXeroConnectRequest
    );
}

function* watchXeroDisconnectRequest() {
    yield takeLatest(
        xeroDisconnectRequestAction.type,
        handleXeroDisconnectRequest
    );
}

function* watchXeroManualSyncRequest() {
    yield takeLatest(
        xeroManualSyncRequestAction.type,
        handleXeroManualSyncRequest
    );
}

function* watchCheckImportJobStatusRequest() {
    yield takeLatest(
        getCheckImportJobStatusRequestAction.type,
        handleCheckImportJobStatusRequest
    );
}

function* watchMYOBConnectRequest() {
    yield takeLatest(
        myobConnectRequestAction.type,
        handleMYOBConnectRequest
    );
}

function* watchMYOBDisconnectRequest() {
    yield takeLatest(
        myobDisconnectRequestAction.type,
        handleMYOBDisconnectRequest
    );
}

function* watchGetMYOBConfigurationRequest() {
    yield takeLatest(
        getMYOBConfigurationRequestAction.type,
        handleGetMYOBConfigurationRequest
    );
}

function* watchMYOBConnectCompanyRequest() {
    yield takeLatest(
        myobConnectMYOBCompanyRequestAction.type,
        handleMYOBConnectCompanyRequest
    );
}

function* watchMYOBManualSyncRequest() {
    yield takeLatest(
        myobManualSyncRequestAction.type,
        handleMYOBManualSyncRequest
    );
}

function* watchGetMYOBCompanySettingsRequest() {
    yield takeLatest(
        getMYOBCompanySettingsRequestAction.type,
        handleGetMYOBCompanySettingsRequest
    );
}

// We can also use `fork()` here to split our saga into multiple watchers.
function* importSaga() {
    yield all([
        fork(watchUploadTemplateFileRequest),
        fork(watchRAUploadTemplateFileRequest),
        fork(watchDownloadTemplateFileRequest),
        fork(watchGetImportHistoryRequest),
        fork(watchGetImportHistoryDataRequest),
        fork(watchSetImportHistorySelectedIdRequest),
        fork(watchGetImportHistoryDataErrorsAndWarningsRequest),
        fork(watchGetXeroConfigurationRequest),
        fork(watchXeroConnectRequest),
        fork(watchXeroDisconnectRequest),
        fork(watchXeroManualSyncRequest),
        fork(watchCheckImportJobStatusRequest),
        fork(watchMYOBConnectRequest),
        fork(watchMYOBDisconnectRequest),
        fork(watchGetMYOBConfigurationRequest),
        fork(watchMYOBConnectCompanyRequest),
        fork(watchMYOBManualSyncRequest),
        fork(watchGetMYOBCompanySettingsRequest)
    ]);
}

export default importSaga;